import React from 'react';
import {
  Checkbox,
  DropdownMenu,
  Icons,
  RadioGroup,
  Typography,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { FilterDropdownMenuMobile } from '@/experiences/sales-portal/components/filter-dropdown-menu-mobile';
import { isWeb } from '@/utils/navigation';
import { Pressable, View } from 'react-native';

const DROPDOWN_MAX_HEIGHT = 400;
const CHECK_ICON_SIZE = 16;
// No xsmall token in sk-ui radii (smallest is 4); 2px keeps row highlight
// visually crisper than the container's rounding.
const ROW_BORDER_RADIUS = 2;
const RADIO_ITEM_HOVER_STYLE = {
  backgroundColor: '$bgPrimaryHover',
  borderRadius: ROW_BORDER_RADIUS,
} as const;

export interface FilterDropdownOption<TKey extends string = string> {
  count?: number;
  /** Preformatted count subtitle shown under the label in the mobile rows. */
  countLabel?: string;
  disabled?: boolean;
  key: TKey;
  label: string;
  selected: boolean;
}

export interface FilterDropdownMenuProps<TKey extends string> {
  isOpen: boolean;
  /**
   * `multi` (default) shows checkboxes; `single` is a RadioGroup with a
   * check-on-right indicator on the selected option.
   */
  mode?: 'multi' | 'single';
  onApply?: (selectedKeys: TKey[]) => void;
  onOpenChange: (open: boolean) => void;
  onToggle: (key: TKey) => void;
  options: FilterDropdownOption<TKey>[];
  /** Mobile only: header above the action-sheet rows; the web popover ignores it. */
  title?: string;
  trigger: React.ReactNode;
}

export const FilterDropdownMenu = <TKey extends string>(
  props: FilterDropdownMenuProps<TKey>,
) =>
  isWeb ? (
    <WebFilterDropdownMenu {...props} />
  ) : (
    <FilterDropdownMenuMobile {...props} />
  );

const WebFilterDropdownMenu = <TKey extends string>({
  isOpen,
  mode = 'multi',
  onOpenChange,
  onToggle,
  options,
  trigger,
}: FilterDropdownMenuProps<TKey>) => {
  const [styles, theme] = useStyles();

  const multiBody = options.map((option) => (
    <Pressable
      accessibilityLabel={
        option.count !== undefined
          ? `${option.label} (${option.count})`
          : option.label
      }
      accessibilityRole="checkbox"
      accessibilityState={{
        checked: option.selected,
        disabled: option.disabled,
      }}
      disabled={option.disabled}
      key={option.key}
      onPress={() => onToggle(option.key)}
      style={({ hovered }) => [
        styles.row,
        hovered && !option.disabled ? styles.rowHovered : null,
      ]}
    >
      <View style={styles.rowLeft}>
        <View pointerEvents="none">
          <Checkbox
            checked={option.selected}
            disabled={option.disabled}
            size="$sm"
          />
        </View>
        <Typography
          color={option.disabled ? '$textDisabled' : '$textPrimary'}
          size="$sm"
        >
          {option.label}
        </Typography>
      </View>
      {option.count !== undefined ? (
        <Typography
          color={option.disabled ? '$textDisabled' : '$textTertiary'}
          size="$sm"
        >
          {option.count}
        </Typography>
      ) : null}
    </Pressable>
  ));

  const singleBody = (
    <RadioGroup
      onValueChange={(value) => onToggle(value as TKey)}
      value={options.find((o) => o.selected)?.key ?? ''}
    >
      {options.map((option) => (
        <RadioGroup.Item
          alignItems="center"
          backgroundColor="transparent"
          borderRadius={ROW_BORDER_RADIUS}
          borderWidth={0}
          disabled={option.disabled}
          flexDirection="row"
          height="auto"
          hoverStyle={RADIO_ITEM_HOVER_STYLE}
          key={option.key}
          paddingHorizontal="$8"
          paddingVertical="$8"
          value={option.key}
          width="100%"
        >
          <Typography
            color={option.disabled ? '$textDisabled' : '$textPrimary'}
            size="$sm"
          >
            {option.label}
          </Typography>
          <View style={styles.singleSpacer} />
          {option.selected ? (
            <Icons.Check
              color={theme.sk.color.icon.primary}
              size={CHECK_ICON_SIZE}
            />
          ) : null}
        </RadioGroup.Item>
      ))}
    </RadioGroup>
  );

  return (
    <DropdownMenu
      onOpenChange={onOpenChange}
      open={isOpen}
      placement="bottom-start"
    >
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Content borderRadius="$4" maxHeight={DROPDOWN_MAX_HEIGHT}>
        {mode === 'single' ? singleBody : multiBody}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

const useStyles = createStyles((theme) => ({
  row: {
    alignItems: 'center',
    borderRadius: ROW_BORDER_RADIUS,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
    justifyContent: 'space-between',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  rowHovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  rowLeft: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  singleSpacer: {
    flex: 1,
    minWidth: theme.sk.spacing.spaceLarge,
  },
}));
